import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, History } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const STATUSES = [
  { v: "not_checked", l: "Niet gecontroleerd" },
  { v: "pending", l: "In behandeling" },
  { v: "checked", l: "Gecontroleerd" },
  { v: "issues_found", l: "Aandachtspunten gevonden" },
  { v: "additional_information_required", l: "Aanvullende informatie nodig" },
];

const DEFAULT_SCOPE = ["energielabel", "eigendomsakte"];

/** Admin-only panel: zet documentcheck-status + bekijk audit log. */
export const AdminDocumentCheck = ({ lid, listing, onSaved }) => {
  const [form, setForm] = useState({
    status: listing?.document_check_status || "not_checked",
    scope: (listing?.document_check_scope?.length ? listing.document_check_scope : DEFAULT_SCOPE).join(", "),
    provider: listing?.document_check_provider || "",
    date: listing?.document_check_date ? listing.document_check_date.slice(0, 10) : "",
    notes: listing?.document_check_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  useEffect(() => {
    setForm({
      status: listing?.document_check_status || "not_checked",
      scope: (listing?.document_check_scope?.length ? listing.document_check_scope : DEFAULT_SCOPE).join(", "),
      provider: listing?.document_check_provider || "",
      date: listing?.document_check_date ? listing.document_check_date.slice(0, 10) : "",
      notes: listing?.document_check_notes || "",
    });
  }, [listing]);

  const loadAudit = async () => {
    setShowAudit((v) => !v);
    if (audit) return;
    try {
      const r = await api(`/admin/sale-listings/${lid}/audit`);
      setAudit(r.events || []);
    } catch (e) { toast.error(e.message); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const scope = form.scope.split(",").map((s) => s.trim()).filter(Boolean);
      const payload = {
        status: form.status,
        scope,
        provider: form.provider || null,
        date: form.date ? new Date(form.date).toISOString() : null,
        notes: form.notes || null,
      };
      const updated = await api(`/admin/sale-listings/${lid}/document-check`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      toast.success("Documentcheck opgeslagen");
      onSaved?.(updated);
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-6 rounded-3xl border border-border bg-white p-6 shadow-sm" data-testid="admin-doc-check">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="text-brandblue" />
          <h3 className="font-display text-lg font-bold text-navy">Documentcheck (optioneel)</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={loadAudit} data-testid="doc-check-audit-toggle">
          <History size={14} className="mr-1.5" /> {showAudit ? "Verberg audit" : "Bekijk audit"}
        </Button>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Alleen zichtbaar als "Documenten professioneel gecontroleerd" op de publieke pagina na status "Gecontroleerd" of "Aandachtspunten gevonden". Interne notities zijn niet publiek.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs font-semibold text-slate-600">Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid="doc-check-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-600">Uitvoerder (bv. externe professional)</Label>
          <Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="Naam of partner-ID" className="mt-1 rounded-xl" data-testid="doc-check-provider" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs font-semibold text-slate-600">Scope (komma-gescheiden)</Label>
          <Input value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} placeholder="energielabel, eigendomsakte" className="mt-1 rounded-xl" data-testid="doc-check-scope" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-600">Datum (leeg = vandaag)</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-xl" data-testid="doc-check-date" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs font-semibold text-slate-600">Interne notities (niet publiek)</Label>
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1 rounded-xl" data-testid="doc-check-notes" />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-xl bg-navy text-amber-300 hover:bg-navy/90" data-testid="doc-check-save">
          {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <ShieldCheck size={14} className="mr-1.5" />}
          Documentcheck opslaan
        </Button>
      </div>

      {showAudit && (
        <div className="mt-5 rounded-2xl border border-border bg-slate-50 p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Audit log</h4>
          {!audit ? (
            <div className="mt-2 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Laden…</div>
          ) : audit.length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">Geen events gelogd voor deze advertentie.</p>
          ) : (
            <ul className="mt-2 space-y-1.5 text-[12px]">
              {audit.map((e) => (
                <li key={e.id} className="rounded-lg bg-white p-2.5">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-navy">{e.event}</span>
                    <span className="text-slate-400">{new Date(e.timestamp).toLocaleString("nl-NL")}</span>
                  </div>
                  {e.changed_fields?.length ? (
                    <div className="mt-0.5 text-slate-500">Velden: {e.changed_fields.join(", ")}</div>
                  ) : null}
                  {e.terms_version && (
                    <div className="text-slate-400">Terms v{e.terms_version} · Verklaring {e.declaration_version}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDocumentCheck;
